import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  APIError,
  artifactIDFromJob,
  commitResume,
  createProjection,
  createRender,
  createRenderProjection,
  createResume,
  downloadArtifactContent,
  getArtifact,
  getDocumentIngestion,
  getRenderJob,
  listVersions,
  loadLatestSession,
  retryDocumentIngestion,
  validateVersion,
  type Artifact,
  type DocumentIngestionStatus,
  type RenderProfile,
  type SessionRecord,
  type ValidationReport,
  type VersionRecord,
} from "./api";
import { ImportCenter } from "./ImportCenter.tsx";
import {
  buildInitialState,
  buildPatchOperations,
  createBlankDraft,
  createEducation,
  createExperience,
  createProject,
  createSkill,
  draftFromProjection,
  validateDraft,
  validateDraftFormats,
  type EducationDraft,
  type ExperienceDraft,
  type ProjectDraft,
  type Projection,
  type ResumeDraft,
  type SkillDraft,
} from "./model";
import type { ResumeImportResult } from "./resume-import.ts";
import "./resident-display.css";
import "./ingestion-register.css";
import "./typography-refresh.css";
import "./interaction-feedback.css";

const storageKey = "zeitpoint.resume.session.v0";
type Section = "basics" | "experience" | "education" | "skills" | "projects";
type SaveState = "idle" | "loading" | "saving" | "saved" | "error" | "conflict";
type PreviewProfile = "a4" | "letter" | "ats";
type ExportPhase = "idle" | "starting" | "queued" | "running" | "ready" | "downloading" | "failed";

interface ExportRun {
  phase: ExportPhase;
  detail: string;
  jobId?: string | undefined;
  artifact?: Artifact | undefined;
}

const exportOptions: Array<{ profile: RenderProfile; label: string; detail: string }> = [
  { profile: "swiss.visual.a4", label: "Visual · A4 PDF", detail: "Swiss Grid · international A4" },
  { profile: "swiss.visual.letter", label: "Visual · Letter PDF", detail: "Swiss Grid · US Letter" },
  { profile: "resume.ats.pdf", label: "ATS · PDF", detail: "Linear layout for machine parsing" },
  { profile: "resume.ats.docx", label: "ATS · DOCX", detail: "Editable Word-compatible document" },
];

const sections: Array<{ id: Section; label: string; index: string }> = [
  { id: "basics", label: "Basics", index: "01" },
  { id: "experience", label: "Experience", index: "02" },
  { id: "education", label: "Education", index: "03" },
  { id: "skills", label: "Skills", index: "04" },
  { id: "projects", label: "Projects", index: "05" },
];

export function EditorApp({ onSignOut }: { onSignOut?: () => void } = {}) {
  const [draft, setDraft] = useState<ResumeDraft>(createBlankDraft);
  const [projection, setProjection] = useState<Projection | null>(null);
  const [session, setSession] = useState<SessionRecord | null>(null);
  const [versions, setVersions] = useState<VersionRecord[]>([]);
  const [validation, setValidation] = useState<ValidationReport | null>(null);
  const [activeSection, setActiveSection] = useState<Section>("basics");
  const [saveState, setSaveState] = useState<SaveState>("loading");
  const [message, setMessage] = useState("Restoring your latest session…");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [previewProfile, setPreviewProfile] = useState<PreviewProfile>("a4");
  const [exportRuns, setExportRuns] = useState<Partial<Record<RenderProfile, ExportRun>>>({});
  const [ingestion, setIngestion] = useState<DocumentIngestionStatus | null>(null);
  const [ingestionBusy, setIngestionBusy] = useState(false);
  const [ingestionMessage, setIngestionMessage] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);
  const exportControllers = useRef(new Map<RenderProfile, AbortController>());

  useEffect(() => {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) {
      setSaveState("idle");
      setMessage("New resume — not saved yet");
      return;
    }
    let parsed: SessionRecord;
    try {
      parsed = JSON.parse(stored) as SessionRecord;
    } catch {
      window.localStorage.removeItem(storageKey);
      setSaveState("idle");
      setMessage("Start a new resume");
      return;
    }
    void restore(parsed);
  }, []);

  useEffect(() => () => {
    for (const controller of exportControllers.current.values()) controller.abort();
    exportControllers.current.clear();
  }, []);

  const completion = useMemo(() => {
    const checks = [draft.identity.name, draft.profile.summary];
    const filled = checks.filter((value) => value.trim()).length;
    return Math.round((filled / checks.length) * 100);
  }, [draft.identity.name, draft.profile.summary]);

  const liveFormatErrors = useMemo(() => validateDraftFormats(draft), [draft]);
  const visibleErrors = useMemo(
    () => ({ ...fieldErrors, ...liveFormatErrors }),
    [fieldErrors, liveFormatErrors],
  );

  async function restore(candidate: SessionRecord) {
    setSaveState("loading");
    setMessage("Loading the latest version…");
    try {
      const latest = await loadLatestSession(candidate.documentId);
      await refresh(latest);
      setSaveState("saved");
      setMessage("Latest version loaded");
    } catch (error) {
      showError(error, "Could not open the saved resume.");
    }
  }

  async function refresh(nextSession: SessionRecord) {
    const [nextProjection, nextValidation, nextVersions, nextIngestion] = await Promise.all([
      createProjection(nextSession),
      validateVersion(nextSession),
      listVersions(nextSession.documentId),
      getDocumentIngestion(nextSession.documentId),
    ]);
    setSession(nextSession);
    setProjection(nextProjection);
    setDraft(draftFromProjection(nextProjection));
    setValidation(nextValidation);
    setVersions(nextVersions);
    setIngestion(nextIngestion);
    setIngestionMessage("");
    abortExports();
    setExportRuns({});
    window.localStorage.setItem(storageKey, JSON.stringify(nextSession));
  }

  async function refreshVersionHistory() {
    if (!session) return;
    try {
      setVersions(await listVersions(session.documentId));
    } catch (error) {
      const detail = error instanceof APIError ? error.detail : error instanceof Error ? error.message : "The server did not return version history.";
      setMessage(`Version history unavailable: ${detail}`);
    }
  }

  async function save() {
    const errors = validateDraft(draft);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSaveState("error");
      setMessage("Review the required fields");
      window.setTimeout(() => document.querySelector<HTMLElement>("[aria-invalid='true']")?.focus(), 0);
      return;
    }
    setSaveState("saving");
    setMessage("Saving a new version…");
    try {
      let nextSession: SessionRecord;
      if (!session || !projection) {
        nextSession = await createResume(buildInitialState(draft));
      } else {
        const operations = buildPatchOperations(projection, draft);
        if (operations.length === 0) {
          setSaveState("saved");
          setMessage("All changes are already saved");
          return;
        }
        nextSession = await commitResume(session, operations);
      }
      await refresh(nextSession);
      setFieldErrors({});
      setSaveState("saved");
      setMessage(`Version ${nextSession.versionId.slice(0, 18)} saved`);
    } catch (error) {
      if (error instanceof APIError && error.status === 409) {
        setSaveState("conflict");
        setMessage("A newer version is already available on the server");
      } else {
        showError(error, "Could not save the resume.");
      }
    }
  }

  function showError(error: unknown, fallback: string) {
    setSaveState("error");
    setMessage(error instanceof APIError ? `${error.message}: ${error.detail}` : error instanceof Error ? error.message : fallback);
  }

  function updateExportRun(profile: RenderProfile, patch: Partial<ExportRun>) {
    setExportRuns((current) => ({
      ...current,
      [profile]: { phase: "idle", detail: "Not generated", ...current[profile], ...patch },
    }));
  }

  function abortExports() {
    for (const controller of exportControllers.current.values()) controller.abort();
    exportControllers.current.clear();
  }

  async function startExport(profile: RenderProfile) {
    if (!session || saveState !== "saved" || !validation?.valid) {
      updateExportRun(profile, { phase: "failed", detail: "Save and validate the current version first." });
      return;
    }
    exportControllers.current.get(profile)?.abort();
    const controller = new AbortController();
    exportControllers.current.set(profile, controller);
    updateExportRun(profile, { phase: "starting", detail: "Preparing an authorized render projection…", artifact: undefined, jobId: undefined });
    try {
      const renderProjection = await createRenderProjection(session, controller.signal);
      let job = await createRender(session, renderProjection.id, profile, controller.signal);
      updateExportRun(profile, { phase: job.status === "running" ? "running" : "queued", detail: exportJobDetail(job.status), jobId: job.id });
      for (let poll = 0; poll < 120; poll += 1) {
        if (job.status === "completed") {
          const artifactID = artifactIDFromJob(job);
          if (!artifactID) throw new Error("The completed render did not publish an artifact.");
          const detail = await getArtifact(artifactID, controller.signal);
          updateExportRun(profile, { phase: "ready", detail: formatArtifactDetail(detail.artifact), jobId: job.id, artifact: detail.artifact });
          return;
        }
        if (job.status === "failed" || job.status === "preflight_failed" || job.status === "cancelled") {
          throw new Error(renderFailure(job.error, job.status));
        }
        await pollDelay(1000, controller.signal);
        job = await getRenderJob(job.id, controller.signal);
        updateExportRun(profile, { phase: job.status === "running" ? "running" : "queued", detail: exportJobDetail(job.status), jobId: job.id });
      }
      throw new Error("Rendering is taking longer than expected. Retry to check with a new job.");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      updateExportRun(profile, { phase: "failed", detail: exportErrorMessage(error) });
    } finally {
      if (exportControllers.current.get(profile) === controller) exportControllers.current.delete(profile);
    }
  }

  async function downloadExport(profile: RenderProfile) {
    const artifact = exportRuns[profile]?.artifact;
    if (!artifact) return;
    const controller = new AbortController();
    exportControllers.current.get(profile)?.abort();
    exportControllers.current.set(profile, controller);
    updateExportRun(profile, { phase: "downloading", detail: "Checking Export authority and downloading…", artifact });
    try {
      const blob = await downloadArtifactContent(artifact, controller.signal);
      const objectURL = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectURL;
      anchor.download = artifact.safe_filename;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectURL), 1000);
      updateExportRun(profile, { phase: "ready", detail: `Downloaded · ${formatArtifactDetail(artifact)}`, artifact });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      updateExportRun(profile, { phase: "failed", detail: exportErrorMessage(error), artifact });
    } finally {
      if (exportControllers.current.get(profile) === controller) exportControllers.current.delete(profile);
    }
  }

  async function retryIngestion() {
    if (!session || ingestionBusy) return;
    setIngestionBusy(true);
    setIngestionMessage("Requesting a fresh malware scan…");
    try {
      await retryDocumentIngestion(session.documentId);
      const next = await getDocumentIngestion(session.documentId);
      setIngestion(next);
      setIngestionMessage("Retry accepted · waiting for scanner intake");
    } catch (error) {
      setIngestionMessage(error instanceof APIError
        ? `${error.message}: ${error.detail}`
        : error instanceof Error ? error.message : "The retry request failed.");
    } finally {
      setIngestionBusy(false);
    }
  }

  function startNew() {
    if (session && !window.confirm("Start a new resume? The current resume will remain saved on the server.")) return;
    window.localStorage.removeItem(storageKey);
    abortExports();
    setExportRuns({});
    setDraft(createBlankDraft());
    setProjection(null);
    setSession(null);
    setVersions([]);
    setValidation(null);
    setIngestion(null);
    setIngestionMessage("");
    setFieldErrors({});
    setActiveSection("basics");
    setSaveState("idle");
    setMessage("New resume — not saved yet");
  }

  function applyImportedDraft(result: ResumeImportResult): boolean {
    const hasCurrentContent = sections.some((section) => sectionHasContent(section.id, draft));
    if ((session || hasCurrentContent) && !window.confirm(
      "Import this JSON as a new resume? Your saved resume will stay on the server, but unsaved changes in the editor will be replaced.",
    )) return false;
    window.localStorage.removeItem(storageKey);
    abortExports();
    setExportRuns({});
    setDraft(result.draft);
    setProjection(null);
    setSession(null);
    setVersions([]);
    setValidation(null);
    setIngestion(null);
    setIngestionMessage("");
    setFieldErrors({});
    setActiveSection("basics");
    setSaveState("idle");
    setMessage(result.warnings.length > 0
      ? `JSON imported · review ${result.warnings.length} format ${result.warnings.length === 1 ? "warning" : "warnings"}`
      : "JSON imported · review the draft, then create the resume");
    return true;
  }

  function markDirty(next: ResumeDraft) {
    abortExports();
    setExportRuns({});
    setDraft(next);
    setValidation(null);
    if (Object.keys(fieldErrors).length > 0) setFieldErrors(validateDraft(next));
    setSaveState("idle");
    setMessage(session ? "You have unsaved changes" : "Complete the required fields, then save");
  }

  function clearActiveSection() {
    if (!sectionHasContent(activeSection, draft)) return;
    const label = sections.find((item) => item.id === activeSection)?.label ?? "Section";
    if (!window.confirm(`Clear ${label}? All content in this section will be removed. Other sections will stay unchanged.`)) return;
    let next = draft;
    switch (activeSection) {
      case "basics":
        next = {
          ...draft,
          identity: { ...draft.identity, name: "", headline: "" },
          contact: { ...draft.contact, email: "", phone: "", location: "", website: "" },
          profile: { ...draft.profile, summary: "" },
        };
        break;
      case "experience":
        next = { ...draft, experiences: [] };
        break;
      case "education":
        next = { ...draft, education: [] };
        break;
      case "skills":
        next = { ...draft, skills: [] };
        break;
      case "projects":
        next = { ...draft, projects: [] };
        break;
    }
    markDirty(next);
    setMessage(`${label} cleared · other sections unchanged`);
  }

  function updateExperience(id: string, patch: Partial<ExperienceDraft>) {
    markDirty({ ...draft, experiences: draft.experiences.map((item) => item.id === id ? { ...item, ...patch } : item) });
  }

  function updateEducation(id: string, patch: Partial<EducationDraft>) {
    markDirty({ ...draft, education: draft.education.map((item) => item.id === id ? { ...item, ...patch } : item) });
  }

  function updateSkill(id: string, patch: Partial<SkillDraft>) {
    markDirty({ ...draft, skills: draft.skills.map((item) => item.id === id ? { ...item, ...patch } : item) });
  }

  function updateProject(id: string, patch: Partial<ProjectDraft>) {
    markDirty({ ...draft, projects: draft.projects.map((item) => item.id === id ? { ...item, ...patch } : item) });
  }

  const busy = saveState === "loading" || saveState === "saving";

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand" aria-label="ZEITPOINT BEP">
          <span className="brand-mark" aria-hidden="true">ZP</span>
          <span><strong>ZEITPOINT</strong><small>BEP resident display · semantic resume</small></span>
        </div>
        <div className={`save-status save-status--${saveState}`} role="status" aria-live="polite">
          <span className="status-dot" aria-hidden="true" />
          <span>{message}</span>
        </div>
        <div className="topbar-actions">
          {onSignOut && <button className="button button--quiet" type="button" onClick={onSignOut} disabled={busy}>Sign out</button>}
          <button className="button button--quiet button--import" type="button" onClick={() => setImportOpen(true)} disabled={busy}>Import</button>
          <button className="button button--quiet" type="button" onClick={() => setExportsOpen(true)}>Exports</button>
          <button className="button button--quiet" type="button" onClick={startNew} disabled={busy}>New</button>
          <button className="button button--primary" type="button" onClick={() => void save()} disabled={busy}>
            {saveState === "saving" ? "Saving…" : session ? "Save version" : "Create resume"}
          </button>
        </div>
      </header>

      <div className="workspace">
        <aside className="rail" aria-label="Resume sections">
          <div className="progress-block">
            <div className="progress-label"><span>Core completed</span><strong>{completion}%</strong></div>
            <div className="progress-track" aria-hidden="true"><span style={{ width: `${completion}%` }} /></div>
          </div>
          <nav>
            {sections.map((section) => (
              <button key={section.id} type="button" className={activeSection === section.id ? "rail-link is-active" : "rail-link"} onClick={() => setActiveSection(section.id)}>
                <span className="rail-title"><span className="rail-index" aria-hidden="true">{section.index}</span><span>{section.label}</span></span>
                <span className="rail-count" aria-label={`Entries: ${sectionCount(section.id, draft)}`}>{sectionCount(section.id, draft)}</span>
              </button>
            ))}
            <VersionHistory versions={versions} onOpen={() => void refreshVersionHistory()} />
          </nav>
        </aside>

        <main className="editor" aria-busy={busy}>
          {saveState === "loading" ? <EditorSkeleton /> : (
            <>
              {saveState === "conflict" && (
                <div className="notice notice--warning" role="alert">
                  <div><strong>Newer version found</strong><p>We did not overwrite it. Load the server version, then reapply your changes.</p></div>
                  <button className="button button--quiet" type="button" onClick={() => session && void restore(session)}>Load version</button>
                </div>
              )}
              {saveState === "error" && message && <div className="notice notice--error" role="alert">{message}</div>}
              <section className="editor-section" aria-labelledby={`${activeSection}-title`}>
                <SectionHeader
                  section={activeSection}
                  onClear={clearActiveSection}
                  clearDisabled={busy || !sectionHasContent(activeSection, draft)}
                />
                {activeSection === "basics" && (
                  <div className="form-stack">
                    <div className="field-row">
                      <Field label="Full name" value={draft.identity.name} onChange={(value) => markDirty({ ...draft, identity: { ...draft.identity, name: value } })} error={visibleErrors[`${draft.identity.id}.name`]} autoComplete="name" />
                      <Field label="Professional headline" value={draft.identity.headline} onChange={(value) => markDirty({ ...draft, identity: { ...draft.identity, headline: value } })} placeholder="For example, Lead Product Designer" />
                    </div>
                    <TextArea label="Professional summary" value={draft.profile.summary} onChange={(value) => markDirty({ ...draft, profile: { ...draft.profile, summary: value } })} error={visibleErrors[`${draft.profile.id}.summary`]} hint="Write 2–4 sentences about your focus, scope, and strengths." rows={5} />
                    <div className="subsection-heading"><h3>Contact details</h3><p>Shown in both visual and ATS formats.</p></div>
                    <div className="field-row">
                      <Field label="Email" type="email" value={draft.contact.email} onChange={(value) => markDirty({ ...draft, contact: { ...draft.contact, email: value } })} error={visibleErrors[`${draft.contact.id}.email`]} hint="Format: name@example.com. Spaces are not allowed." autoComplete="email" />
                      <Field label="Phone" type="tel" value={draft.contact.phone} onChange={(value) => markDirty({ ...draft, contact: { ...draft.contact, phone: value } })} hint="International format works best, for example +49 30 1234567." autoComplete="tel" />
                    </div>
                    <div className="field-row">
                      <Field label="City / region" value={draft.contact.location} onChange={(value) => markDirty({ ...draft, contact: { ...draft.contact, location: value } })} autoComplete="address-level2" />
                      <Field label="Website or portfolio" type="url" value={draft.contact.website} onChange={(value) => markDirty({ ...draft, contact: { ...draft.contact, website: value } })} error={visibleErrors[`${draft.contact.id}.website`]} hint="Include https://, for example https://example.com." placeholder="https://" />
                    </div>
                  </div>
                )}

                {activeSection === "experience" && (
                  <Collection title="Experience" empty="Add a position to build the chronological experience section." onAdd={() => markDirty({ ...draft, experiences: [...draft.experiences, createExperience()] })}>
                    {draft.experiences.map((item, index) => (
                      <Entry key={item.id} title={item.role || `Position ${index + 1}`} subtitle={item.organization} onRemove={() => markDirty({ ...draft, experiences: draft.experiences.filter((entry) => entry.id !== item.id) })} onMoveUp={() => markDirty({ ...draft, experiences: moveItem(draft.experiences, index, -1) })} onMoveDown={() => markDirty({ ...draft, experiences: moveItem(draft.experiences, index, 1) })} canMoveUp={index > 0} canMoveDown={index < draft.experiences.length - 1}>
                        <div className="field-row"><Field label="Organization" value={item.organization} onChange={(value) => updateExperience(item.id, { organization: value })} error={visibleErrors[`${item.id}.organization`]} /><Field label="Role" value={item.role} onChange={(value) => updateExperience(item.id, { role: value })} error={visibleErrors[`${item.id}.role`]} /></div>
                        <div className="field-row field-row--dates"><Field label="Start" value={item.startDate} onChange={(value) => updateExperience(item.id, { startDate: value })} placeholder="2022-03" hint="YYYY, YYYY-MM, or YYYY-MM-DD." error={visibleErrors[`${item.id}.startDate`]} /><Field label="End" value={item.endDate} onChange={(value) => updateExperience(item.id, { endDate: value })} placeholder="2025-08" hint="YYYY, YYYY-MM, or YYYY-MM-DD." disabled={item.current} error={visibleErrors[`${item.id}.endDate`]} /><Check label="Currently working here" checked={item.current} onChange={(current) => updateExperience(item.id, { current, endDate: current ? "" : item.endDate })} /></div>
                        <Field label="Location" value={item.location} onChange={(value) => updateExperience(item.id, { location: value })} />
                        <TextArea label="Summary" value={item.summary} onChange={(value) => updateExperience(item.id, { summary: value })} rows={3} />
                        <TextArea label="Achievements" value={item.bullets.join("\n")} onChange={(value) => updateExperience(item.id, { bullets: lines(value) })} hint="Add one achievement per line." rows={4} />
                      </Entry>
                    ))}
                  </Collection>
                )}

                {activeSection === "education" && (
                  <Collection title="Education" empty="Add an institution and qualification." onAdd={() => markDirty({ ...draft, education: [...draft.education, createEducation()] })}>
                    {draft.education.map((item, index) => (
                      <Entry key={item.id} title={item.qualification || `Education entry ${index + 1}`} subtitle={item.institution} onRemove={() => markDirty({ ...draft, education: draft.education.filter((entry) => entry.id !== item.id) })} onMoveUp={() => markDirty({ ...draft, education: moveItem(draft.education, index, -1) })} onMoveDown={() => markDirty({ ...draft, education: moveItem(draft.education, index, 1) })} canMoveUp={index > 0} canMoveDown={index < draft.education.length - 1}>
                        <div className="field-row"><Field label="Institution" value={item.institution} onChange={(value) => updateEducation(item.id, { institution: value })} error={visibleErrors[`${item.id}.institution`]} /><Field label="Qualification" value={item.qualification} onChange={(value) => updateEducation(item.id, { qualification: value })} error={visibleErrors[`${item.id}.qualification`]} /></div>
                        <div className="field-row"><Field label="Field of study" value={item.field} onChange={(value) => updateEducation(item.id, { field: value })} /><Field label="Location" value={item.location} onChange={(value) => updateEducation(item.id, { location: value })} /></div>
                        <div className="field-row field-row--dates"><Field label="Start" value={item.startDate} onChange={(value) => updateEducation(item.id, { startDate: value })} placeholder="2014" hint="YYYY, YYYY-MM, or YYYY-MM-DD." error={visibleErrors[`${item.id}.startDate`]} /><Field label="End" value={item.endDate} onChange={(value) => updateEducation(item.id, { endDate: value })} placeholder="2016" hint="YYYY, YYYY-MM, or YYYY-MM-DD." disabled={item.current} error={visibleErrors[`${item.id}.endDate`]} /><Check label="Currently studying here" checked={item.current} onChange={(current) => updateEducation(item.id, { current, endDate: current ? "" : item.endDate })} /></div>
                        <TextArea label="Additional details" value={item.details} onChange={(value) => updateEducation(item.id, { details: value })} rows={3} />
                      </Entry>
                    ))}
                  </Collection>
                )}

                {activeSection === "skills" && (
                  <Collection title="Skills" empty="Add key competencies that should be discoverable by ATS." onAdd={() => markDirty({ ...draft, skills: [...draft.skills, createSkill()] })} compact>
                    {draft.skills.map((item, index) => (
                      <Entry key={item.id} title={item.name || `Skill ${index + 1}`} subtitle={item.level} onRemove={() => markDirty({ ...draft, skills: draft.skills.filter((entry) => entry.id !== item.id) })} onMoveUp={() => markDirty({ ...draft, skills: moveItem(draft.skills, index, -1) })} onMoveDown={() => markDirty({ ...draft, skills: moveItem(draft.skills, index, 1) })} canMoveUp={index > 0} canMoveDown={index < draft.skills.length - 1} compact>
                        <div className="field-row"><Field label="Skill" value={item.name} onChange={(value) => updateSkill(item.id, { name: value })} error={visibleErrors[`${item.id}.name`]} /><Field label="Level" value={item.level} onChange={(value) => updateSkill(item.id, { level: value })} placeholder="For example, advanced" /></div>
                        <Field label="Keywords" value={item.keywords.join(", ")} onChange={(value) => updateSkill(item.id, { keywords: commaList(value) })} hint="Separate with commas for semantic search and ATS matching." />
                      </Entry>
                    ))}
                  </Collection>
                )}

                {activeSection === "projects" && (
                  <Collection title="Projects" empty="Add an independent or professional project." onAdd={() => markDirty({ ...draft, projects: [...draft.projects, createProject()] })}>
                    {draft.projects.map((item, index) => (
                      <Entry key={item.id} title={item.name || `Project ${index + 1}`} subtitle={item.url} onRemove={() => markDirty({ ...draft, projects: draft.projects.filter((entry) => entry.id !== item.id) })} onMoveUp={() => markDirty({ ...draft, projects: moveItem(draft.projects, index, -1) })} onMoveDown={() => markDirty({ ...draft, projects: moveItem(draft.projects, index, 1) })} canMoveUp={index > 0} canMoveDown={index < draft.projects.length - 1}>
                        <div className="field-row"><Field label="Name" value={item.name} onChange={(value) => updateProject(item.id, { name: value })} error={visibleErrors[`${item.id}.name`]} /><Field label="Link" type="url" value={item.url} onChange={(value) => updateProject(item.id, { url: value })} error={visibleErrors[`${item.id}.url`]} hint="Include https://, for example https://example.com/project." placeholder="https://" /></div>
                        <TextArea label="Description" value={item.description} onChange={(value) => updateProject(item.id, { description: value })} rows={3} />
                        <div className="field-row"><Field label="Start" value={item.startDate} onChange={(value) => updateProject(item.id, { startDate: value })} placeholder="2024-01" hint="YYYY, YYYY-MM, or YYYY-MM-DD." error={visibleErrors[`${item.id}.startDate`]} /><Field label="End" value={item.endDate} onChange={(value) => updateProject(item.id, { endDate: value })} placeholder="2024-09" hint="YYYY, YYYY-MM, or YYYY-MM-DD." error={visibleErrors[`${item.id}.endDate`]} /></div>
                        <TextArea label="Outcomes" value={item.bullets.join("\n")} onChange={(value) => updateProject(item.id, { bullets: lines(value) })} hint="Add one outcome per line." rows={4} />
                      </Entry>
                    ))}
                  </Collection>
                )}
              </section>
            </>
          )}
        </main>

        <aside className="preview-panel" aria-label="Resume preview">
          <div className="preview-toolbar">
            <div><strong>Preview</strong><small>Draft preview · not an official artifact</small></div>
            <div className="segmented" aria-label="Preview format">
              {(["a4", "letter", "ats"] as const).map((profile) => <button key={profile} type="button" aria-pressed={previewProfile === profile} onClick={() => setPreviewProfile(profile)}>{profile === "letter" ? "Letter" : profile.toUpperCase()}</button>)}
            </div>
          </div>
          <div className="preview-content">
            <ResumePreview draft={draft} profile={previewProfile} />
            <aside className="preview-readouts" aria-label="Document status">
              <div className="validation-summary">
                <span className={validation?.valid ? "validation-mark is-valid" : "validation-mark"} aria-hidden="true">{validation?.valid ? "✓" : "·"}</span>
                <div><strong>{validation?.valid ? "Semantic validation passed" : session ? "Revalidation required" : "Validation runs after save"}</strong><small>{validation ? `${validation.domain_ruleset} · ${validation.issues.length} ${validation.issues.length === 1 ? "issue" : "issues"}` : "Resume Domain v0.1"}</small></div>
              </div>
              <IngestionRegister
                status={ingestion}
                busy={ingestionBusy}
                message={ingestionMessage}
                onRetry={() => void retryIngestion()}
              />
              <section className="readout-placeholder" aria-label="Reserved document workspace">
                <header className="readout-placeholder__header">
                  <span aria-hidden="true">08</span>
                  <div>
                    <h2>Document workspace</h2>
                    <p>Reserved operational area</p>
                  </div>
                </header>
                <div className="readout-placeholder__body">
                  <span aria-hidden="true">·</span>
                  <p><strong>Ready for the next module</strong><small>Review and document intelligence controls will appear here.</small></p>
                </div>
              </section>
            </aside>
          </div>
        </aside>
      </div>
      <ImportCenter open={importOpen} onClose={() => setImportOpen(false)} onApply={applyImportedDraft} />
      <ExportCenter
        open={exportsOpen}
        onClose={() => setExportsOpen(false)}
        enabled={Boolean(session && saveState === "saved" && validation?.valid)}
        runs={exportRuns}
        onStart={(profile) => void startExport(profile)}
        onDownload={(profile) => void downloadExport(profile)}
      />
    </div>
  );
}

function SectionHeader({ section, onClear, clearDisabled }: { section: Section; onClear: () => void; clearDisabled: boolean }) {
  const copy: Record<Section, [string, string]> = {
    basics: ["Basics", "Name, positioning, and contacts are the required foundation for every format."],
    experience: ["Experience", "Facts stay semantic, without manually managed columns or coordinates."],
    education: ["Education", "Qualifications and dates flow into both visual and ATS formats."],
    skills: ["Skills", "A concise competency vocabulary for readers and machine matching."],
    projects: ["Projects", "Work that strengthens your experience and demonstrates measurable outcomes."],
  };
  const index = sections.find((item) => item.id === section)?.index ?? "00";
  return <header className="section-header"><span className="section-index" aria-hidden="true">{index}</span><div className="section-header-copy"><h1 id={`${section}-title`}>{copy[section][0]}</h1><p>{copy[section][1]}</p></div><button type="button" className="button button--clear-section" onClick={onClear} disabled={clearDisabled}>Clear section</button></header>;
}

function Field({ label, value, onChange, error, hint, type = "text", placeholder, autoComplete, disabled = false }: { label: string; value: string; onChange: (value: string) => void; error?: string | undefined; hint?: string; type?: string; placeholder?: string; autoComplete?: string; disabled?: boolean }) {
  const uid = useId();
  const id = `field-${uid.replace(/:/g, "")}`;
  const description = [error ? `${id}-error` : "", hint ? `${id}-hint` : ""].filter(Boolean).join(" ") || undefined;
  return <label className="field" htmlFor={id}><span>{label}</span><input id={id} type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} autoComplete={autoComplete} disabled={disabled} aria-invalid={Boolean(error)} aria-describedby={description} />{error && <small id={`${id}-error`} className="field-error" role="status">{error}</small>}{hint && <small id={`${id}-hint`} className="field-hint">{hint}</small>}</label>;
}

function TextArea({ label, value, onChange, error, hint, rows }: { label: string; value: string; onChange: (value: string) => void; error?: string | undefined; hint?: string; rows: number }) {
  const uid = useId();
  const id = `field-${uid.replace(/:/g, "")}`;
  const description = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return <label className="field" htmlFor={id}><span>{label}</span><textarea id={id} value={value} onChange={(event) => onChange(event.target.value)} rows={rows} aria-invalid={Boolean(error)} aria-describedby={description} />{error ? <small id={`${id}-error`} className="field-error">{error}</small> : hint ? <small id={`${id}-hint`}>{hint}</small> : null}</label>;
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return <label className="check"><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} /><span>{label}</span></label>;
}

function Collection({ title, empty, onAdd, children, compact = false }: { title: string; empty: string; onAdd: () => void; children: React.ReactNode; compact?: boolean }) {
  const hasChildren = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return <div className={compact ? "collection collection--compact" : "collection"}><div className="collection-toolbar"><h2>{title}</h2><button type="button" className="button button--add" onClick={onAdd}><span aria-hidden="true">+</span> Add</button></div>{hasChildren ? <div className="entry-list">{children}</div> : <div className="empty-state"><strong>This section is empty</strong><p>{empty}</p><button type="button" className="button button--primary" onClick={onAdd}>Add entry</button></div>}</div>;
}

function Entry({ title, subtitle, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, children, compact = false }: { title: string; subtitle: string; onRemove: () => void; onMoveUp: () => void; onMoveDown: () => void; canMoveUp: boolean; canMoveDown: boolean; children: React.ReactNode; compact?: boolean }) {
  return <article className={compact ? "entry entry--compact" : "entry"}><header><div><h3>{title}</h3>{subtitle && <p>{subtitle}</p>}</div><div className="entry-actions"><button type="button" className="move-button" onClick={onMoveUp} disabled={!canMoveUp} aria-label={`Move up: ${title}`}>Up</button><button type="button" className="move-button" onClick={onMoveDown} disabled={!canMoveDown} aria-label={`Move down: ${title}`}>Down</button><button type="button" className="remove-button" onClick={onRemove} aria-label={`Remove: ${title}`}>Remove</button></div></header><div className="entry-fields">{children}</div></article>;
}

function VersionHistory({ versions, onOpen }: { versions: VersionRecord[]; onOpen: () => void }) {
  const formatter = new Intl.DateTimeFormat("en-US", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  const currentVersion = versions[0];
  return <details className="history" onToggle={(event) => event.currentTarget.open && onOpen()}>
    <summary className="rail-link history-toggle">
      <span className="rail-title"><span className="rail-index" aria-hidden="true">06</span><span>History</span></span>
      <span className="rail-count" aria-label={currentVersion ? `Current version: ${currentVersion.sequence}` : "No saved versions"}>{currentVersion ? `v${currentVersion.sequence}` : "—"}</span>
    </summary>
    <div className="history-body">
      {versions.length === 0
        ? <p>Version history appears after your first save.</p>
        : <ol>{versions.slice(0, 5).map((version, index) => <li key={version.id}><span className="history-sequence">v{version.sequence}</span><span><strong>{index === 0 ? "Current" : "Saved"}</strong><small>{formatter.format(new Date(version.created_at))}</small></span></li>)}</ol>}
    </div>
  </details>;
}

function IngestionRegister({ status, busy, message, onRetry }: {
  status: DocumentIngestionStatus | null;
  busy: boolean;
  message: string;
  onRetry: () => void;
}) {
  const latest = status?.admissions[0];
  const phase = !status ? "idle" : !status.source ? "native" : latest?.state ?? "pending";
  const title = ingestionTitle(phase);
  const detail = message || ingestionDetail(status, latest);
  const retryable = Boolean(latest?.retryable);
  return <section className={`ingestion-register ingestion-register--${phase}`} aria-labelledby="ingestion-register-title">
    <header className="ingestion-heading">
      <span aria-hidden="true">07</span>
      <div><h2 id="ingestion-register-title">Source admission</h2><p>Content-free security and intake evidence</p></div>
    </header>
    <div className="ingestion-readout">
      <span className="ingestion-lamp" aria-hidden="true" />
      <div className="ingestion-copy">
        <strong>{title}</strong>
        <small role="status" aria-live="polite">{detail}</small>
      </div>
      {retryable && <button type="button" className="button button--ingestion" onClick={onRetry} disabled={busy}>
        {busy ? "Requesting…" : "Retry scan"}
      </button>}
    </div>
    {latest && <dl className="ingestion-metrics">
      <div><dt>Scanner</dt><dd>{latest.scanner_name} / {latest.scanner_version}</dd></div>
      <div><dt>Attempt</dt><dd>{latest.attempt}</dd></div>
      <div><dt>Evidence</dt><dd>{latest.verdict ?? latest.error_code ?? "scan pending"}</dd></div>
    </dl>}
  </section>;
}

function ingestionTitle(phase: string): string {
  if (phase === "native") return "Native semantic document";
  if (phase === "pending") return "Source awaiting admission";
  if (phase === "scanning") return "Malware scan in progress";
  if (phase === "accepted") return "Source admitted";
  if (phase === "quarantine") return "Source quarantined";
  if (phase === "rejected") return "Source rejected";
  return "Available after first save";
}

function ingestionDetail(status: DocumentIngestionStatus | null, latest: DocumentIngestionStatus["admissions"][number] | undefined): string {
  if (!status) return "Save the resume to load operational evidence.";
  if (!status.source) return "This resume is authored directly in BEP; no external source is linked.";
  if (!latest) return `Binding ${status.source.sync_state} · waiting for scanner evidence.`;
  if (latest.state === "accepted") return "Clean verdict recorded; parser admission is enabled.";
  if (latest.state === "quarantine") return latest.retryable ? "Transient scanner failure; authorized retry is available." : "Admission requires operator review.";
  if (latest.state === "rejected") return "Malware evidence is terminal and cannot be replayed.";
  return "Untrusted source bytes remain isolated until a clean verdict is recorded.";
}

function ExportCenter({ open, onClose, enabled, runs, onStart, onDownload }: {
  open: boolean;
  onClose: () => void;
  enabled: boolean;
  runs: Partial<Record<RenderProfile, ExportRun>>;
  onStart: (profile: RenderProfile) => void;
  onDownload: (profile: RenderProfile) => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return <dialog
    ref={dialogRef}
    className="export-center"
    aria-labelledby="export-center-title"
    onCancel={(event) => { event.preventDefault(); onClose(); }}
    onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
  >
    <header className="export-center-header">
      <div><span aria-hidden="true">EX</span><div><h2 id="export-center-title">Export center</h2><p>Immutable artifacts from the current saved version</p></div></div>
      <button type="button" className="import-close" onClick={onClose} aria-label="Close export center">Close</button>
    </header>
    <OfficialExports enabled={enabled} runs={runs} onStart={onStart} onDownload={onDownload} />
  </dialog>;
}

function OfficialExports({ enabled, runs, onStart, onDownload }: {
  enabled: boolean;
  runs: Partial<Record<RenderProfile, ExportRun>>;
  onStart: (profile: RenderProfile) => void;
  onDownload: (profile: RenderProfile) => void;
}) {
  return <section className="export-panel" aria-labelledby="official-exports-title">
    <header className="export-heading">
      <span aria-hidden="true">08</span>
      <div><h2 id="official-exports-title">Official exports</h2><p>{enabled ? "Generate immutable, preflight-checked artifacts from the saved version." : "Save a valid version to enable official artifacts."}</p></div>
    </header>
    <div className="export-list">
      {exportOptions.map((option) => {
        const run = runs[option.profile] ?? { phase: "idle" as const, detail: option.detail };
        const active = run.phase === "starting" || run.phase === "queued" || run.phase === "running" || run.phase === "downloading";
        const downloadable = Boolean(run.artifact) && (run.phase === "ready" || run.phase === "failed");
        const label = active ? exportActionLabel(run.phase) : downloadable ? "Download" : run.phase === "failed" ? "Retry" : "Generate";
        return <div className={`export-row export-row--${run.phase}`} key={option.profile}>
          <div className="export-copy">
            <div><span className="export-state" aria-hidden="true" /><strong>{option.label}</strong></div>
            <small role="status" aria-live="polite">{run.detail || option.detail}</small>
          </div>
          <button
            type="button"
            className={downloadable ? "button button--export is-ready" : "button button--export"}
            onClick={() => downloadable ? onDownload(option.profile) : onStart(option.profile)}
            disabled={active || (!enabled && run.phase !== "failed")}
          >
            {label}
          </button>
        </div>;
      })}
    </div>
    <p className="export-note">Downloads require a fresh Export authorization check. An artifact ID alone never grants access.</p>
  </section>;
}

function ResumePreview({ draft, profile }: { draft: ResumeDraft; profile: PreviewProfile }) {
  const contacts = [draft.contact.email, draft.contact.phone, draft.contact.location, draft.contact.website].filter(Boolean);
  return <div className={`resume-stage resume-stage--${profile}`}><article className="resume-sheet">
    <header className="resume-head"><h2>{draft.identity.name || "YOUR NAME"}</h2><p>{draft.identity.headline || "Professional headline"}</p>{contacts.length > 0 && <div className="resume-contact">{contacts.map((item) => <span key={item}>{item}</span>)}</div>}</header>
    {draft.profile.summary && <ResumeSection index="01" title="Profile"><p>{draft.profile.summary}</p></ResumeSection>}
    {draft.experiences.length > 0 && <ResumeSection index="02" title="Experience">{draft.experiences.map((item) => <div className="resume-item" key={item.id}><div><h4>{item.role || "Role"}</h4><strong>{item.organization || "Organization"}</strong></div><time>{dateRange(item.startDate, item.endDate, item.current)}</time>{item.summary && <p>{item.summary}</p>}{item.bullets.length > 0 && <ul>{item.bullets.map((bullet, index) => <li key={`${item.id}-${index}`}>{bullet}</li>)}</ul>}</div>)}</ResumeSection>}
    {draft.education.length > 0 && <ResumeSection index="03" title="Education">{draft.education.map((item) => <div className="resume-item" key={item.id}><div><h4>{item.qualification || "Qualification"}</h4><strong>{item.institution || "Institution"}</strong></div><time>{dateRange(item.startDate, item.endDate, item.current)}</time>{item.details && <p>{item.details}</p>}</div>)}</ResumeSection>}
    {draft.skills.length > 0 && <ResumeSection index="04" title="Skills"><div className="resume-skills">{draft.skills.map((item) => <span key={item.id}>{item.name || "Skill"}{item.level ? ` · ${item.level}` : ""}</span>)}</div></ResumeSection>}
    {draft.projects.length > 0 && <ResumeSection index="05" title="Projects">{draft.projects.map((item) => <div className="resume-item" key={item.id}><div><h4>{item.name || "Project"}</h4>{item.url && <strong>{item.url}</strong>}</div><time>{dateRange(item.startDate, item.endDate, false)}</time>{item.description && <p>{item.description}</p>}</div>)}</ResumeSection>}
  </article></div>;
}

function ResumeSection({ index, title, children }: { index: string; title: string; children: React.ReactNode }) {
  return <section className="resume-section"><div className="resume-section-label"><span aria-hidden="true">{index}</span><h3>{title}</h3></div><div className="resume-section-content">{children}</div></section>;
}

function EditorSkeleton() {
  return <div className="skeleton" aria-label="Loading editor"><span /><span /><span /><span /></div>;
}

function sectionCount(section: Section, draft: ResumeDraft): number {
  if (section === "basics") return [draft.identity.name, draft.profile.summary, draft.contact.email].filter((value) => value.trim()).length;
  if (section === "experience") return draft.experiences.length;
  if (section === "education") return draft.education.length;
  if (section === "skills") return draft.skills.length;
  return draft.projects.length;
}

function sectionHasContent(section: Section, draft: ResumeDraft): boolean {
  if (section === "basics") {
    return [
      draft.identity.name,
      draft.identity.headline,
      draft.profile.summary,
      draft.contact.email,
      draft.contact.phone,
      draft.contact.location,
      draft.contact.website,
    ].some((value) => value.trim().length > 0);
  }
  return sectionCount(section, draft) > 0;
}

function moveItem<T>(items: T[], index: number, offset: -1 | 1): T[] {
  const target = index + offset;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  const current = next[index];
  const adjacent = next[target];
  if (current === undefined || adjacent === undefined) return items;
  next[index] = adjacent;
  next[target] = current;
  return next;
}

function exportActionLabel(phase: ExportPhase): string {
  if (phase === "starting") return "Starting…";
  if (phase === "queued") return "Queued…";
  if (phase === "running") return "Rendering…";
  if (phase === "downloading") return "Downloading…";
  return "Generate";
}

function exportJobDetail(status: string): string {
  if (status === "running") return "Rendering in an isolated worker…";
  if (status === "cancellation_requested") return "Cancellation requested…";
  return "Queued for an isolated render worker…";
}

function renderFailure(error: unknown, status: string): string {
  if (typeof error === "string" && error.trim()) return error;
  if (typeof error === "object" && error !== null) {
    const value = error as { detail?: unknown; message?: unknown; code?: unknown };
    for (const candidate of [value.detail, value.message, value.code]) {
      if (typeof candidate === "string" && candidate.trim()) return candidate;
    }
  }
  return status === "preflight_failed" ? "The generated file did not pass server preflight." : `Render ended with status: ${status}.`;
}

function exportErrorMessage(error: unknown): string {
  if (error instanceof APIError) return `${error.message}: ${error.detail}`;
  return error instanceof Error ? error.message : "The artifact could not be generated.";
}

function formatArtifactDetail(artifact: Artifact): string {
  const size = artifact.size_bytes >= 1_048_576
    ? `${(artifact.size_bytes / 1_048_576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(artifact.size_bytes / 1024))} KB`;
  return `Ready · ${size} · ${artifact.digest.slice(0, 19)}…`;
}

function pollDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException("The render poll was cancelled.", "AbortError"));
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, milliseconds);
    if (signal.aborted) onAbort();
    else signal.addEventListener("abort", onAbort, { once: true });
  });
}

function lines(value: string): string[] { return value.split(/\r?\n/).map((item) => item.trim()).filter(Boolean); }
function commaList(value: string): string[] { return value.split(",").map((item) => item.trim()).filter(Boolean); }
function dateRange(start: string, end: string, current: boolean): string { return [start, current ? "Present" : end].filter(Boolean).join(" — "); }
